import { useCallback, useEffect, useRef, useState } from 'react'
import { getCities, getUfs } from '../../../../../services/api/https/system/system.http'
import { erroCfop } from '../ValoresFiscaisCte/valoresFiscaisCte.util'
import {
  cargaDoCadastro,
  cargaInicial,
  erroCarga,
  erroTomador,
  montarCargaPayload,
  montarPartesPayload,
  parteDoCadastro,
  parteVazia,
  validarParte,
} from './ctePartes.util'

const prestacaoInicial = () => ({
  inicio: { uf: null, city: null, tocado: false },
  fim: { uf: null, city: null, tocado: false },
})

// Vários modais de emissão ficam montados na mesma tela: o prefixo evita ids de radio repetidos
let proximoFormulario = 1

export default function useFormularioCte({
  visible,
  onError,
  origemRemetente = 'empresa',
  origemDestinatario = 'manual',
  destinatarioFixo = false,
}) {
  const [ufs, setUfs] = useState([])
  const [remetente, setRemetente] = useState(parteVazia(origemRemetente))
  const [destinatario, setDestinatario] = useState(
    destinatarioFixo ? null : parteVazia(origemDestinatario),
  )
  const [expedidor, setExpedidor] = useState(null)
  const [recebedor, setRecebedor] = useState(null)
  const [tomador, setTomador] = useState('0')
  const [tomadorOutros, setTomadorOutros] = useState(parteVazia('manual'))
  const [prestacao, setPrestacao] = useState(prestacaoInicial())
  const [cidadesPrestacao, setCidadesPrestacao] = useState({ inicio: [], fim: [] })
  const [carga, setCarga] = useState(cargaInicial())
  const [dadosPendentes, setDadosPendentes] = useState(null)
  const cacheCidades = useRef(new Map())
  const idPrefixo = useRef(`cte${proximoFormulario++}`).current

  const carregarCidades = useCallback(async ufId => {
    if (!ufId) return []
    if (cacheCidades.current.has(ufId)) return cacheCidades.current.get(ufId)
    const lista = await getCities(ufId)
    cacheCidades.current.set(ufId, lista)
    return lista
  }, [])

  useEffect(() => {
    if (!visible) return
    setRemetente(parteVazia(origemRemetente))
    setDestinatario(destinatarioFixo ? null : parteVazia(origemDestinatario))
    setExpedidor(null)
    setRecebedor(null)
    setTomador('0')
    setTomadorOutros(parteVazia('manual'))
    setPrestacao(prestacaoInicial())
    setCarga(cargaInicial())
    setDadosPendentes(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  useEffect(() => {
    if (!visible || ufs.length) return
    getUfs()
      .then(setUfs)
      .catch(error => onError(error))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  // Dados iniciais (cadastro das notas) são aplicados quando as UFs já estão carregadas
  useEffect(() => {
    if (!dadosPendentes || !ufs.length) return
    if (dadosPendentes.remetente) setRemetente(parteDoCadastro(dadosPendentes.remetente, ufs))
    if (dadosPendentes.destinatario && !destinatarioFixo) {
      setDestinatario(parteDoCadastro(dadosPendentes.destinatario, ufs))
    }
    if (dadosPendentes.carga) setCarga(cargaDoCadastro(dadosPendentes.carga))
    setDadosPendentes(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dadosPendentes, ufs])

  const aplicarDadosIniciais = useCallback(dados => setDadosPendentes(dados), [])

  const parteInicio = expedidor || remetente
  const parteFim = recebedor || destinatario
  const ufParteInicio = parteInicio ? parteInicio.uf : null
  const cityParteInicio = parteInicio ? parteInicio.city : null
  const ufParteFim = parteFim ? parteFim.uf : null
  const cityParteFim = parteFim ? parteFim.city : null

  useEffect(() => {
    setPrestacao(prevState =>
      prevState.inicio.tocado
        ? prevState
        : {
            ...prevState,
            inicio: { ...prevState.inicio, uf: ufParteInicio, city: cityParteInicio },
          },
    )
  }, [ufParteInicio, cityParteInicio])

  useEffect(() => {
    if (!parteFim) return
    setPrestacao(prevState =>
      prevState.fim.tocado
        ? prevState
        : { ...prevState, fim: { ...prevState.fim, uf: ufParteFim, city: cityParteFim } },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ufParteFim, cityParteFim])

  const definirFim = useCallback(
    (ufSigla, cityId) => {
      setPrestacao(prevState => {
        if (prevState.fim.tocado) return prevState
        const uf = ufs.find(item => item.uf === ufSigla) || null
        return {
          ...prevState,
          fim: { uf, city: uf && cityId ? { id: cityId, name: '' } : null, tocado: false },
        }
      })
    },
    [ufs],
  )

  const ufInicioId = prestacao.inicio.uf ? prestacao.inicio.uf.id : null
  const ufFimId = prestacao.fim.uf ? prestacao.fim.uf.id : null

  useEffect(() => {
    let ativo = true
    if (!ufInicioId) {
      setCidadesPrestacao(prevState => ({ ...prevState, inicio: [] }))
      return undefined
    }
    carregarCidades(ufInicioId)
      .then(lista => ativo && setCidadesPrestacao(prevState => ({ ...prevState, inicio: lista })))
      .catch(error => ativo && onError(error))
    return () => {
      ativo = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ufInicioId])

  useEffect(() => {
    let ativo = true
    if (!ufFimId) {
      setCidadesPrestacao(prevState => ({ ...prevState, fim: [] }))
      return undefined
    }
    carregarCidades(ufFimId)
      .then(lista => ativo && setCidadesPrestacao(prevState => ({ ...prevState, fim: lista })))
      .catch(error => ativo && onError(error))
    return () => {
      ativo = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ufFimId])

  const onPrestacaoChange = (ponto, patch) => {
    setPrestacao(prevState => ({
      ...prevState,
      [ponto]: { ...prevState[ponto], ...patch, tocado: true },
    }))
  }

  const adicionarExpedidor = () => setExpedidor({ ...remetente })
  const removerExpedidor = () => {
    setExpedidor(null)
    setTomador(atual => (atual === '1' ? '0' : atual))
  }
  const adicionarRecebedor = () =>
    setRecebedor(destinatario ? { ...destinatario } : parteVazia(origemDestinatario))
  const removerRecebedor = () => {
    setRecebedor(null)
    setTomador(atual => (atual === '2' ? '0' : atual))
  }

  // A unidade sugere o tipo de medida (KG/TON: PESO BRUTO, UN: UNIDADES, M3: CUBAGEM, L: LITRAGEM)
  const onUnidadeChange = unidade => {
    setCarga(prevState => ({ ...prevState, unidade, tipo_medida: unidade.tipoMedidaPadrao }))
  }

  const ufInicio = prestacao.inicio.uf ? prestacao.inicio.uf.uf : null
  const ufFim = prestacao.fim.uf ? prestacao.fim.uf.uf : null
  const form = { remetente, destinatario, expedidor, recebedor, tomador, tomadorOutros }

  const validar = ({ cfop, ufEmissor }) => {
    const erroRemetente = validarParte(remetente, 'Remetente')
    if (erroRemetente) return erroRemetente
    if (!destinatarioFixo) {
      const erroDestinatario = validarParte(destinatario, 'Destinatário')
      if (erroDestinatario) return erroDestinatario
    }
    if (expedidor) {
      const erroExpedidor = validarParte(expedidor, 'Expedidor')
      if (erroExpedidor) return erroExpedidor
    }
    if (recebedor) {
      const erroRecebedor = validarParte(recebedor, 'Recebedor')
      if (erroRecebedor) return erroRecebedor
    }
    const erroDoTomador = erroTomador(form)
    if (erroDoTomador) return erroDoTomador
    if (!prestacao.inicio.uf || !prestacao.inicio.city)
      return 'Selecione a UF e a cidade de início da prestação'
    if (!prestacao.fim.uf || !prestacao.fim.city)
      return 'Selecione a UF e a cidade de fim da prestação'
    if (!cfop) return 'Selecione o CFOP'
    const erroDoCfop = erroCfop(cfop.codigo, ufEmissor, ufInicio, ufFim)
    if (erroDoCfop) return erroDoCfop
    return erroCarga(carga)
  }

  const montarPayload = () => ({
    partes: montarPartesPayload(form),
    city_inicio_id: prestacao.inicio.city.id,
    city_fim_id: prestacao.fim.city.id,
    carga: montarCargaPayload(carga),
  })

  return {
    idPrefixo,
    ufs,
    carregarCidades,
    ...form,
    setRemetente,
    setDestinatario,
    setExpedidor,
    setRecebedor,
    setTomador,
    setTomadorOutros,
    adicionarExpedidor,
    removerExpedidor,
    adicionarRecebedor,
    removerRecebedor,
    prestacao,
    cidadesPrestacao,
    onPrestacaoChange,
    definirFim,
    ufInicio,
    ufFim,
    carga,
    setCarga,
    onUnidadeChange,
    aplicarDadosIniciais,
    validar,
    montarPayload,
  }
}
